import { useAtom } from 'jotai';
import { lazy, Suspense, useEffect } from 'react';

import { resetOpenModalCount } from 'src/hooks/use-modal-back-handler';

import store from 'src/store';
import { ModalType } from 'src/store/modal';

import AlertModal from 'src/components/modal/alert-modal';
import PopupModal from 'src/components/modal/popup-modal';
import { SplashScreen } from 'src/components/loading-screen';
import ConfirmModal from 'src/components/modal/confirm-modal';
import ApiErrorModal from 'src/components/modal/api-error-modal';
import JoinPolicyModal from 'src/components/modal/join-policy-modal';
import MoveToNextLessonModal from 'src/components/modal/move-to-next-lesson-modal';

const CommonReportMainModal = lazy(() => import('src/components/modal/common-report-modal'));
const CommonUserBlockingModal = lazy(() => import('src/components/modal/common-user-blocking'));
const CommonReportConfirmationModal = lazy(
  () => import('src/components/modal/common-report-confirm')
);

const ResumeLearningModal = lazy(() => import('src/components/modal/resume-learning-modal'));
const DeleteStoreProductModal = lazy(
  () => import('src/components/modal/delete-store-product-modal')
);
const ConfirmCancelWriteProductModal = lazy(
  () => import('src/components/modal/confirm-cancel-write-product')
);
const ConfirmDeleteSelectedProductModal = lazy(
  () => import('src/components/modal/confirm-delete-selected-product')
);

const NextLessonModal = lazy(() => import('src/components/modal/next-lesson-modal'));
const TimelineContentModal = lazy(() => import('src/components/modal/timeline-content-modal'));
const LessonQuizModal = lazy(() => import('src/components/modal/lesson-quiz-modal'));
const LessonQuizFormModal = lazy(() => import('src/components/modal/lesson-quiz-form-modal'));
const QuestionAnswerModal = lazy(() => import('src/components/modal/question-answer-modal'));
const SequentialLearningModal = lazy(
  () => import('src/components/modal/sequential-learning-modal')
);
const DeleteAnswerModal = lazy(() => import('src/components/modal/delete-answer-check-list-modal'));
const DuplicateEnrollModal = lazy(() => import('src/components/modal/duplicate-enroll-modal'));
const EnrollInformationModal = lazy(() => import('src/components/modal/enroll-information-modal'));
const EnrollConfirmationModal = lazy(
  () => import('src/components/modal/enroll-confirmation-modal')
);
const DegreeNoticeModal = lazy(() => import('src/components/modal/degree-notice'));
const WaitingExamModal = lazy(() => import('src/components/modal/waiting-exam-modal'));
const WaitingSurveyModal = lazy(() => import('src/components/modal/waiting-survey-modal'));
const WaitingLessonModal = lazy(() => import('src/components/modal/waiting-lesson-modal'));
const ConfirmTakeExamModal = lazy(() => import('src/components/modal/confirm-take-exam-modal'));
const ConfirmTakeSurveyModal = lazy(() => import('src/components/modal/confirm-take-survey-modal'));
const LessonRegularEvaluationUnderProgressionModal = lazy(
  () => import('src/components/modal/lesson-regular-eval-under-progression')
);
const MyClassTerminatedClassModal = lazy(
  () => import('src/components/modal/my-class-terminated-class')
);
const MyClassNotInLearningPeriodModal = lazy(
  () => import('src/components/modal/my-class-not-in-learning-period')
);

const TermsPrivacyModal = lazy(() => import('src/components/modal/terms-privacy-modal'));

const AskForDeletaModal = lazy(() => import('src/components/modal/ask-for-delete-modal'));
const MissionConfirmDeleteFeedbackModal = lazy(
  () => import('src/components/modal/mission-confirm-delete-feedback-modal')
);
const MissionConfirmCancelModal = lazy(() => import('src/components/modal/mission-cancel-modal'));
const RewardCertificateDetailMadal = lazy(
  () => import('src/components/modal/reward-certificate-detail-modal')
);
const ExamFeedbackIssue = lazy(() => import('src/components/modal/exam-feedback-issue-modal'));
type Props = {
  children: React.ReactNode;
};
const LessonListModal = lazy(() => import('src/components/modal/lesson-list-modal'));
const LessonResourceModal = lazy(() => import('src/components/modal/lesson-resource-modal'));
const ExamQnaModal = lazy(() => import('src/components/modal/exam-qna-modal'));

const ImagePreviewModal = lazy(() => import('src/components/modal/image-preview-modal'));

const mobileModifyCommentInputModal = lazy(
  () => import('src/components/modal/mobile-modify-comment-input-modal')
);
const ProcessStockModal = lazy(() => import('src/components/modal/process-stock-modal'));

const ModalProvider = ({ children }: Readonly<Props>) => {
  const [modalState, setModalState] = useAtom(store.modal.modalState);

  /**
   * 새로고침 시 히스토리 상태 정리 및 카운터 리셋
   *
   * 새로고침 시나리오:
   * - 모달이 열린 상태에서 새로고침 → React 앱 재마운트
   * - modalState는 초기값(open: false)으로 시작하지만
   * - window.history.state에는 { __modal: true }가 남아있을 수 있음
   * - openModalCount는 전역 변수이므로 새로고침 후에도 유지될 수 있음
   * - 이 경우 히스토리 상태와 카운터를 정리하여 불일치 방지
   */
  useEffect(() => {
    // 새로고침 후 openModalCount 리셋
    resetOpenModalCount();

    // 마운트 시 모달이 닫혀있는데 히스토리에 모달 엔트리가 남아있는 경우 정리
    // 새로고침 후 모달이 닫혀있지만 히스토리에 모달 엔트리가 남아있는 경우
    if (!modalState.open && window.history.state?.__modal) {
      /**
       * history.back()을 사용하여 모달 엔트리를 히스토리 스택에서 제거
       * - 새로고침 후 모달이 없으므로 바로 cleanup 가능
       * - openModalCount는 0으로 리셋되었으므로 안전하게 실행됨
       */
      window.history.back();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 마운트 시에만 실행 (새로고침 후 히스토리 정리)

  // call custom onClose function before closing the modal (reset modal state)
  const onClose = () => {
    if (modalState.onClose) modalState.onClose();
    setModalState((prevState) => ({
      ...prevState, // 최신 상태 참조
      open: false,
      data: undefined,
      type: undefined,
      // 닫힌 뒤에도 이전 모달의 onClose를 상태에 남겨두지 않는다.
      // 새 모달 open 시점의 초기화와 함께, 모달 간 라우팅 콜백 누수를 한 번 더 차단한다.
      onClose: () => {},
    }));
  };

  const render = () => {
    if (!modalState.open || !modalState.type) return null;

    // select corresponding modal component (based on Modal type)
    const ModalComponent = MODAL_COMPONENTS[modalState.type];

    if (!ModalComponent) return null;

    return <ModalComponent open={modalState.open} onClose={onClose} {...modalState.data} />;
  };

  return (
    <>
      {children}
      <Suspense fallback={<SplashScreen />}>{render()}</Suspense>
    </>
  );
};

export default ModalProvider;

// add modal variants using type:component as key:value pair
const MODAL_COMPONENTS: { [key in Exclude<ModalType, undefined>]: any } = {
  resumeLearning: ResumeLearningModal,
  askForDelete: AskForDeletaModal,
  apiError: ApiErrorModal,
  deleteStoreProduct: DeleteStoreProductModal,
  nextLesson: NextLessonModal,
  timelineContent: TimelineContentModal,
  lessonQuiz: LessonQuizModal,
  lessonQuizForm: LessonQuizFormModal,
  questionAnswer: QuestionAnswerModal,
  sequentialLearning: SequentialLearningModal,
  alert: AlertModal,
  confirm: ConfirmModal,
  deleteAnswer: DeleteAnswerModal,
  reportMain: CommonReportMainModal,
  reportConfirm: CommonReportConfirmationModal,
  blockUser: CommonUserBlockingModal,
  enrollConfirmation: EnrollConfirmationModal,
  duplicateEnroll: DuplicateEnrollModal,
  enrollInformation: EnrollInformationModal,
  waitingExam: WaitingExamModal,
  waitingSurvey: WaitingSurveyModal,
  waitingLesson: WaitingLessonModal,
  confirmTakeExam: ConfirmTakeExamModal,
  confirmTakeSurvey: ConfirmTakeSurveyModal,
  termsPrivacy: TermsPrivacyModal,
  missionConfirmDeleteFeedback: MissionConfirmDeleteFeedbackModal,
  missionConfirmCancel: MissionConfirmCancelModal,
  rewardCertificateDetail: RewardCertificateDetailMadal,
  joinPolicy: JoinPolicyModal,
  examFeedbackIssue: ExamFeedbackIssue,
  lessonList: LessonListModal,
  popup: PopupModal,
  lessonResource: LessonResourceModal,
  moveToNextLesson: MoveToNextLessonModal,
  examQna: ExamQnaModal,
  imagePreview: ImagePreviewModal,
  lessonRegularEvaluationUnderProgression: LessonRegularEvaluationUnderProgressionModal,
  productConfirmCancelWrite: ConfirmCancelWriteProductModal,
  productConfirmDeleteSelectedItems: ConfirmDeleteSelectedProductModal,
  mobileModifyCommentInput: mobileModifyCommentInputModal,
  myClassTerminatedClass: MyClassTerminatedClassModal,
  myClassNotInLearningPeriod: MyClassNotInLearningPeriodModal,
  degreeNotice: DegreeNoticeModal,
  processStock: ProcessStockModal,
};
